/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars, @typescript-eslint/no-explicit-any, react/no-array-index-key, no-nested-ternary */
/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Button,
  Tooltip,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Shuffle } from 'lucide-react';
import type { PromptDebuggerShow, SectionData } from '../types';
import { FormTextField } from '../../../shared/components/forms';
import { GenreSelector } from '../../shows/components/GenreSelector';
import { LanguageSelector } from '../../../shared/components/LanguageSelector';
import { PromptService } from '../services/promptService';

interface ShowIdeaSectionProps {
  selectedShow?: string;
  topic?: string;
  selectedGenre?: string;
  selectedLanguage?: string;
  onShowChange: (showId: string) => void;
  onTopicChange: (topic: string) => void;
  onGenreChange: (genreId: string) => void;
  onLanguageChange: (languageKey: string) => void;
  children?: React.ReactNode; // For the prompt component
  responseData?: Partial<SectionData['showIdeas']>; // Response data from debug console
}

export const ShowIdeaSection: React.FC<ShowIdeaSectionProps> = ({
  selectedShow,
  topic,
  selectedGenre,
  selectedLanguage,
  onShowChange,
  onTopicChange,
  onGenreChange,
  onLanguageChange,
  children,
  responseData,
}) => {
  const notify = useNotify();

  const [shows, setShows] = useState<PromptDebuggerShow[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [ideas, setIdeas] = useState<string[]>([]);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Load shows on mount
  useEffect(() => {
    loadShows();
  }, []);

  const loadShows = async () => {
    setLoading(true);
    try {
      const showList = await PromptService.getShows();
      setShows(showList);
    } catch (error) {
      // Error loading shows - silently fail for now
    } finally {
      setLoading(false);
    }
  };

  const handleShowSelect = (showId: string) => {
    // Call the parent's onShowChange handler which will handle all the logic
    // including genre auto-selection, topic population, and show data updates
    onShowChange(showId);
  };

  // Update ideas when response data changes (from debug console)
  useEffect(() => {
    if (responseData) {
      if (responseData.ideas && Array.isArray(responseData.ideas)) {
        setIdeas(responseData.ideas);
        setGenerateError(null);
        setTabValue(0); // Switch to Fields tab to show results

        const firstIdea = responseData.ideas[0];
        const incomingTopic =
          responseData.topic ||
          (firstIdea && typeof firstIdea === 'object' && 'topic' in firstIdea
            ? firstIdea.topic
            : '');

        // Update topic if present; genre remains input-driven
        if (incomingTopic && incomingTopic !== topic) {
          onTopicChange(incomingTopic);
        }
      } else {
        setGenerateError('Invalid response format');
      }
    }
  }, [responseData, topic, onTopicChange]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRandomShow = () => {
    if (shows.length === 0) return;

    const randomIndex = Math.floor(Math.random() * shows.length);
    const randomShow = shows[randomIndex];

    handleShowSelect(randomShow.id);
  };

  const handleTopicChange = (newTopic: string) => {
    onTopicChange(newTopic);
  };

  const handleCopyParam = (paramName: string) => {
    const textToCopy = `{${paramName}}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        notify(`Copied: ${textToCopy}`, { type: 'success' });
      })
      .catch(() => {
        notify('Failed to copy to clipboard', { type: 'error' });
      });
  };

  return (
    <Box>
      {/* Input Fields Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {/* Show Selection */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={selectedShow || ''}
                onChange={e => handleShowSelect(e.target.value)}
                disabled={loading}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Populate from a show</em>
                </MenuItem>
                {shows.map(show => (
                  <MenuItem key={show.id} value={show.id}>
                    {show.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<Shuffle />}
              onClick={handleRandomShow}
              disabled={loading || shows.length === 0}
              size="small"
            >
              Random
            </Button>
          </Box>
        </Box>

        {/* Genre Selection */}
        <Box>
          <GenreSelector
            selectedGenreId={selectedGenre}
            onGenreChange={onGenreChange}
            width="full"
          />
        </Box>

        {/* Language Selection */}
        <Box>
          <LanguageSelector
            value={selectedLanguage || 'en'}
            onChange={value => onLanguageChange(value ?? 'en')}
            width="small"
          />
        </Box>

        {/* Topic Field */}
        <Box>
          <FormTextField
            label="Show Topic"
            value={topic || ''}
            onChange={e => handleTopicChange(e.target.value)}
            placeholder="Enter the topic or theme for the show content generation..."
            rows={3}
            width="full"
          />
          <Box sx={{ mt: 0.5 }}>
            <Tooltip title="Click to copy parameter name" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() => handleCopyParam('show-topic')}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {'{show-topic}'}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Prompt Section */}
      {children && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Show Idea Generation Prompt
          </Typography>
          <Box>{children}</Box>
        </Box>
      )}

      {/* Results Section */}
      {(ideas.length > 0 || generateError) && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Generated Ideas
          </Typography>

          <Paper sx={{ mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Fields" />
              <Tab label="JSON" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {tabValue === 0 && (
                <Box>
                  {generateError ? (
                    <Alert severity="error">{generateError}</Alert>
                  ) : ideas.length > 0 ? (
                    <List>
                      {ideas.map((idea, index) => {
                        const ideaText =
                          typeof idea === 'object' && 'topic' in idea
                            ? idea.topic
                            : typeof idea === 'string'
                              ? idea
                              : '';
                        const keyStr =
                          typeof idea === 'object'
                            ? idea.topic?.slice(0, 20) || `idea-${index}`
                            : idea.slice(0, 20);

                        return (
                          <ListItem
                            key={`idea-${index}-${keyStr}`}
                            divider={index < ideas.length - 1}
                          >
                            <ListItemText
                              primary={`Idea ${index + 1}${index === 0 ? ' (Selected)' : ''}`}
                              secondary={ideaText}
                              secondaryTypographyProps={{
                                sx: {
                                  fontWeight: index === 0 ? 'bold' : 'normal',
                                  color:
                                    index === 0
                                      ? 'primary.main'
                                      : 'text.secondary',
                                },
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography color="text.secondary">
                      No ideas generated yet
                    </Typography>
                  )}
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Raw JSON Response:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      {ideas.length > 0
                        ? JSON.stringify({ shows: ideas }, null, 2)
                        : 'No response data'}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};
